import React, { FC, useCallback, useMemo } from 'react';
import { Container, Section } from '@printdeal/ui-library';
import { useSegmentation } from '@printdeal/segmentation/react';
import { useLocation } from '@gatsbyjs/reach-router';
import { useInView } from 'react-intersection-observer';
import SupplementalBanner from '../banner/SupplementalBanner/SupplementalBanner';
import { HomePageBannerData } from '../banner/types';
import { getAudiences, getQueryParamFromLocation } from '../../../templates/homepage/homepage-helper';
import { handleEventOnSupplementalBannerOnViewVariantGA4 } from '../banner/SupplementalBanner/SupplementalBanner.events';
import { HomepageSectionProps } from '../types';
import { getSupplementalBanners } from '../helpers';

export interface SupplementalBannerSectionProps {
  section: HomepageSectionProps,
}

export const SupplementalBannerSection: FC<SupplementalBannerSectionProps> = ({
  section,
}) => {
  const location = useLocation();
  const { traits, isSuccess: isAudienceLoadedFromSegment } = useSegmentation();
  const audienceOverRideForPlayground = getQueryParamFromLocation(location, 'variant');
  /**
   * Memoizing audience data
   */
  const audiencesDataFromSegment = useMemo(
    () => getAudiences(traits, isAudienceLoadedFromSegment, audienceOverRideForPlayground),
    [isAudienceLoadedFromSegment, traits, audienceOverRideForPlayground],
  );

  const {
    supplementalBanners,
  } = useMemo(
    () => ({
      supplementalBanners: getSupplementalBanners(section, 'supplementary_banner') as HomePageBannerData[],
    }),
    [section],
  );

  const handleFeaturedProductListInView = useCallback((inView: boolean) => {
    const bannerDataForViewEvent = supplementalBanners as HomePageBannerData[];
    if (inView) {
      handleEventOnSupplementalBannerOnViewVariantGA4(
        audiencesDataFromSegment.type,
        bannerDataForViewEvent,
      );
    }
  }, [supplementalBanners, audiencesDataFromSegment]);

  const { ref } = useInView({
    threshold: 1,
    onChange: handleFeaturedProductListInView,
    triggerOnce: true,
  });

  if (supplementalBanners.length === 0) {
    return null;
  }

  return (
    <Section
      className="pd-pb-14 pd-pt-0"
      variant={section?.backgroundStyle || 'white'}
      ref={ref}
    >
      {(supplementalBanners.length > 1) ? (
        <Container
          className="pd-grid pd-gap-8 lg:pd-grid-cols-2"
        >
          {supplementalBanners && supplementalBanners.map((bannerData: HomePageBannerData, index: number) => (
            <SupplementalBanner
              audiencesDataFromSegment={audiencesDataFromSegment}
              bannerData={bannerData}
              bannerIndex={index + 1}
            />
          ))}
        </Container>
      )
        : (
          <Container>
            <SupplementalBanner
              audiencesDataFromSegment={audiencesDataFromSegment}
              bannerData={supplementalBanners[0]}
              bannerIndex={1}
            />
          </Container>
        )}
    </Section>
  );
};
